import gql from 'graphql-tag'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import React, { useContext } from 'react'
import { useParams } from 'react-router'
import { useQuery } from '../../api/graphql'
import Place from '../../api/places/model'
import { placeFragment } from '../../graphql/fragments'
import CommunitiesContextProvider from '../../hooks/CommunitiesContextProvider'
import usePlaceFormData from '../../hooks/usePlaceFormData'
import { CurrentUserContext } from '../../providers/CurrentUserContext'
import Routes from '../../startup/routes'
import useRecordLookup from '../../hooks/useRecordLookup'
import { RecordLoadFailed, RecordNotFound } from '../components/misc/RecordNotFound'
import PlaceFormComponent from '../components/places/PlaceFormComponent'

const PlaceEditQuery = gql`
  query PlaceEditQuery($slug: String!) {
    place(slug: $slug) {
      ...placeFragment
    }
  }
  ${placeFragment}
`

const PLACE_EDIT = gql`
  mutation PlaceUpdate($input: PlaceUpdateInput!) {
    placeUpdate(input: $input) {
      place {
        ...placeFragment
      }
      errors {
        attribute
        message
      }
    }
  }
  ${placeFragment}
`

const PlaceEditContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { loading: loadingCurrentUser } = useContext(CurrentUserContext)
  const { data, loading } = usePlaceFormData()

  const { data: placeData, loading: loadingPlace, error: placeError } = useQuery(PlaceEditQuery, {
    variables: { slug: id },
    skip: loadingCurrentUser,
  })

  // {...place} is needed to convert Place to JS object w/o functions
  // for example `omitBy(isNil)` assigned place.fullAddress as a value
  const place = new Place(omitBy({ ...placeData?.place }, isNil))
  place.contact = omitBy(get(place, 'contact'), isNil)
  place.address = omitBy(get(place, 'address'), isNil)
  place.socialUrls = omitBy(get(place, 'socialUrls'), isNil)
  place.primaryCommunityId = place.community?.id

  const isAnythingLoading = loading || loadingCurrentUser || loadingPlace

  // An edit link whose place is removed, never existed or isn't one you can see (CVL-138).
  const lookup = useRecordLookup({ loading: loadingCurrentUser || loadingPlace, error: placeError, record: placeData?.place })
  if (lookup === 'failed') return <RecordLoadFailed />
  if (lookup === 'notFound') return <RecordNotFound kind="place" backTo={Routes.yourPlaces()} backLabel="Back to Your Places" />

  return (
    <CommunitiesContextProvider value={data?.communities}>
      <PlaceFormComponent
        action="update"
        loading={isAnythingLoading}
        mutationQuery={PLACE_EDIT}
        place={isAnythingLoading ? undefined : place}
      />
    </CommunitiesContextProvider>
  )
}

export default PlaceEditContainer
